/* eslint-disable no-param-reassign */
import moment from 'moment';
import ApiClient from 'ApiClient';
import { createSlice } from '@reduxjs/toolkit';

const confirmedDealsReducer = createSlice({
  name: 'confirmedDeals',
  initialState: {
    deals: [],
    loading: false,
    error: null,
    viewType: 'pipeline', // 'pipeline' or 'published'
    filters: {
      // Time period key (for TimePeriodFilterExtended)
      timePeriod: 'this_month', // Default to this_month for pipeline view
      // Date range - default to this month for pipeline
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      // Search by creator name, coupon code, or media content title (filtered on frontend)
      search: '',
      // New/Rebuy filter: [] means all, ['New'], ['Rebuy'], or ['New', 'Rebuy']
      newOrRebuy: [],
      // Confidence filter: [] means all, ['High'], ['Medium'], ['Low'], or combinations
      confidence: [],
      // Country filter: [] means all countries
      country: [],
      // Exclude Published/Deal Paid deal stages
      excludePublished: false,
      // Conversion calculation toggles (backend recompute on change), default OFF
      includeHistoricalConversions: false,
      includeFutureConversions: false,
      includePreviouslyPublishedConversions: false,
    },
    countryOptions: [], // Program country options from HubSpot
    lastFetched: null,
  },
  reducers: {
    fetchDealsStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchDealsSuccess(state, action) {
      state.deals = action.payload.deals;
      state.loading = false;
      state.error = null;
      state.lastFetched = new Date().toISOString();
    },
    fetchDealsError(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    setCountryOptions(state, action) {
      state.countryOptions = action.payload;
    },
    setViewType(state, action) {
      state.viewType = action.payload;
      // Reset deals when switching views to force refetch
      state.deals = [];
      state.lastFetched = null;
      // Reset all filters to defaults based on view type
      state.filters = {
        timePeriod: 'this_month',
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        search: '',
        newOrRebuy: [],
        confidence: [],
        country: [],
        excludePublished: false,
        includeHistoricalConversions: false,
        includeFutureConversions: false,
        includePreviouslyPublishedConversions: false,
      };
    },
    setFilter(state, action) {
      const { property, value } = action.payload;
      state.filters[property] = value;
    },
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    setDateRangeAction(state, action) {
      const { startDate, endDate, timePeriod } = action.payload;
      state.filters.startDate = startDate;
      state.filters.endDate = endDate;
      if (timePeriod) {
        state.filters.timePeriod = timePeriod;
      }
    },
    resetFilters(state) {
      // Reset filters based on current view type
      state.filters = {
        timePeriod: 'this_month',
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        search: '',
        newOrRebuy: [],
        confidence: [],
        country: [],
        excludePublished: false,
        includeHistoricalConversions: false,
        includeFutureConversions: false,
        includePreviouslyPublishedConversions: false,
      };
    },
    updateDeal(state, action) {
      const { dealId, updates } = action.payload;
      const dealIndex = state.deals.findIndex(deal => deal.dealId === dealId);
      if (dealIndex !== -1) {
        state.deals[dealIndex] = { ...state.deals[dealIndex], ...updates };
      }
    },
    updateDealsByProspectId(state, action) {
      const { prospectId, updates } = action.payload;
      console.log('Redux updateDealsByProspectId called:', { prospectId, updates, dealCount: state.deals.length });
      let updatedCount = 0;
      const prospectIdStr = prospectId ? prospectId.toString() : null;
      state.deals.forEach((deal, index) => {
        const dealProspectIdStr = deal.prospectId ? deal.prospectId.toString() : null;
        console.log('Checking deal:', deal.dealId, 'prospectId:', dealProspectIdStr, 'vs', prospectIdStr);
        if (dealProspectIdStr && prospectIdStr && dealProspectIdStr === prospectIdStr) {
          console.log('Updating deal:', deal.dealId, 'with prospect loading:', updates.prospect?.approvalStatus?.ai?.loading);
          state.deals[index] = { ...state.deals[index], ...updates };
          updatedCount++;
        }
      });
      console.log('Updated', updatedCount, 'deals with prospectId:', prospectIdStr);
    },
  },
});

// API call
const fetchDealsApi = async (viewType, filters) => {
  const api = new ApiClient();

  return api.client.post('/api/brand/deals/post-fetch-confirmed-deals', {
    viewType,
    filters: {
      contentReleaseDate: {
        from: filters.startDate,
        to: filters.endDate,
      },
      historicalConversions: Boolean(filters.includeHistoricalConversions),
      extendConversions: Boolean(filters.includeFutureConversions),
      includePreviouslyPublishedConversions: Boolean(filters.includePreviouslyPublishedConversions),
    },
  });
};

const fetchCountryOptionsApi = async () => {
  const api = new ApiClient();
  return api.client.get('/api/brand/get-program-country-options-for-deals');
};

export const {
  fetchDealsStart,
  fetchDealsSuccess,
  fetchDealsError,
  setCountryOptions,
  setViewType,
  setFilter,
  setFilters,
  setDateRangeAction,
  resetFilters,
  updateDeal,
  updateDealsByProspectId,
} = confirmedDealsReducer.actions;

// Thunks
export const fetchDeals = (forceRefresh = false) => async (dispatch, getState) => {
  const state = getState();
  const { viewType, filters, lastFetched, loading } = state.confirmedDeals;

  // Skip if already loading
  if (loading) return;

  // Skip if data was fetched less than 5 minutes ago (unless forced)
  if (!forceRefresh && lastFetched && moment().diff(moment(lastFetched), 'minutes') < 5) {
    return;
  }

  try {
    dispatch(fetchDealsStart());
    const response = await fetchDealsApi(viewType, filters);

    if (response.data.success) {
      dispatch(fetchDealsSuccess({ deals: response.data.result }));
    } else {
      dispatch(fetchDealsError({ error: response.data.error || 'Failed to fetch deals' }));
    }
  } catch (err) {
    dispatch(fetchDealsError({ error: err.message || 'Failed to fetch deals' }));
  }
};

export const changeViewType = viewType => async (dispatch) => {
  dispatch(setViewType(viewType));
  dispatch(fetchDeals(true));
};

export const fetchCountryOptions = () => async (dispatch, getState) => {
  const state = getState();
  // Only fetch if not already loaded
  if (state.confirmedDeals.countryOptions.length > 0) return;

  try {
    const response = await fetchCountryOptionsApi();
    if (response.data.success && response.data.options) {
      // Store full option objects with {value, label} to match deal.programCountry values
      const co = response.data.options;
      // programCountry column cell value in DealsTable.js defaults to empty string
      co.unshift({ value:'', label: '-- Not Assigned --' });
      dispatch(setCountryOptions(co));
    }
  } catch (err) {
    // Silently fail, country filter will just be empty
    console.error('Failed to fetch country options:', err);
  }
};

export const updateFilter = (property, value) => async (dispatch) => {
  dispatch(setFilter({ property, value }));
};

// For conversion-calculation toggles that require a backend recompute
export const updateCalculationFilter = (property, value) => async (dispatch) => {
  dispatch(setFilter({ property, value }));
  dispatch(fetchDeals(true));
};

export const updateFilters = filtersUpdate => async (dispatch) => {
  dispatch(setFilters(filtersUpdate));
};

export const setDateRange = ({ startDate, endDate, timePeriod }) => async (dispatch) => {
  dispatch(setDateRangeAction({ startDate, endDate, timePeriod }));
  dispatch(fetchDeals(true));
};

// Selectors
export const selectDeals = state => state.confirmedDeals.deals;
export const selectLoading = state => state.confirmedDeals.loading;
export const selectError = state => state.confirmedDeals.error;
export const selectViewType = state => state.confirmedDeals.viewType;
export const selectFilters = state => state.confirmedDeals.filters;

// Published-stage deals define the "published" set (shared by the table and the stats)
const PUBLISHED_STAGES = ['Published', 'Deal Paid'];

// Filtered deals selector (applies frontend filters: search, newOrRebuy, confidence, country)
export const selectFilteredDeals = (state) => {
  const { deals, filters, viewType } = state.confirmedDeals;

  return deals.filter((deal) => {
    // The "Previously Published Content" summary row is pinned separately in the table
    if (deal.isPreviouslyPublishedContent) {
      return false;
    }

    // In published view, only show Published/Deal Paid rows in the table
    if (viewType === 'published' && !PUBLISHED_STAGES.includes(deal.dealStage)) {
      return false;
    }

    // Search filter (creator name, promo code, or media content title)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const title = deal.creator?.title?.toLowerCase() || '';
      const creatorName = deal.creator?.name?.toLowerCase() || '';
      const promoCode = deal.promoCode?.toLowerCase() || '';
      const mediaContentTitle = deal.mediaContentTitle?.toLowerCase() || '';
      if (!title.includes(searchLower) &&
          !creatorName.includes(searchLower) &&
          !promoCode.includes(searchLower) &&
          !mediaContentTitle.includes(searchLower)) {
        return false;
      }
    }

    // New/Rebuy filter (case-insensitive)
    if (filters.newOrRebuy && filters.newOrRebuy.length > 0) {
      const dealType = deal.newOrExistingCreator?.toLowerCase() === 'new' ? 'New' : 'Rebuy';
      if (!filters.newOrRebuy.includes(dealType)) {
        return false;
      }
    }

    // Confidence filter (case-insensitive)
    if (filters.confidence && filters.confidence.length > 0) {
      if (!deal.confidence) return false;
      const dealConfidence = deal.confidence.charAt(0).toUpperCase() + deal.confidence.slice(1).toLowerCase();
      if (!filters.confidence.includes(dealConfidence)) {
        return false;
      }
    }

    // Country filter
    if (filters.country && filters.country.length > 0) {
      if (!filters.country.includes(deal.programCountry)) {
        return false;
      }
    }

    // Exclude Published/Deal Paid stages
    if (filters.excludePublished) {
      const EXCLUDE_STAGES = ['Published', 'Deal Paid'];
      if (EXCLUDE_STAGES.includes(deal.dealStage)) {
        return false;
      }
    }

    return true;
  });
};

// Selector for the pinned "Previously Published Content" summary row (or null)
export const selectPreviouslyPublishedRow = state =>
  state.confirmedDeals.deals.find(deal => deal.isPreviouslyPublishedContent) || null;

// Stats selector (calculates stats from all deals, unaffected by filters)
export const selectStats = (state) => {
  const allDeals = state.confirmedDeals.deals;
  // Deal-based stats exclude the summary row; conversions total includes it.
  const filteredDeals = allDeals.filter(deal => !deal.isPreviouslyPublishedContent);

  // Published: published-stage deals (same set the table shows)
  const today = moment().format('YYYY-MM-DD');
  const publishedDeals = filteredDeals.filter(deal => PUBLISHED_STAGES.includes(deal.dealStage));
  // Tracked: all deals in any recognised stage (denominator for % Published)
  const trackedCount = filteredDeals.length;

  // Booked Amount: sum of totalContractAmount for all filtered deals
  const bookedByCurrency = {};
  filteredDeals.forEach((deal) => {
    const currency = deal.currency || 'USD';
    if (!bookedByCurrency[currency]) bookedByCurrency[currency] = 0;
    bookedByCurrency[currency] += deal.totalContractAmount || 0;
  });

  // Published Amount: only deals with a real publish date on/before today contribute;
  // a published-stage deal without an actual publish date counts as $0 (regardless of CRD)
  const publishedByCurrency = {};
  publishedDeals.forEach((deal) => {
    const currency = deal.currency || 'USD';
    if (!publishedByCurrency[currency]) publishedByCurrency[currency] = 0;
    const hasRealPublishDate = deal.publishDate && deal.publishDate <= today;
    publishedByCurrency[currency] += hasRealPublishDate ? (deal.totalContractAmount || 0) : 0;
  });

  const percentPublished = trackedCount > 0
    ? Math.round((publishedDeals.length / trackedCount) * 100)
    : 0;

  // New vs Rebuy stats (case-insensitive matching, from all filtered deals)
  const totalDeals = filteredDeals.length;
  const newCreatorDeals = filteredDeals.filter(deal =>
    deal.newOrExistingCreator?.toLowerCase() === 'new'
  );
  const rebuyCreatorDeals = filteredDeals.filter(deal =>
    deal.newOrExistingCreator?.toLowerCase() !== 'new' && deal.newOrExistingCreator
  );
  const percentNew = totalDeals > 0 ? Math.round((newCreatorDeals.length / totalDeals) * 100) : 0;
  const percentRebuy = totalDeals > 0 ? Math.round((rebuyCreatorDeals.length / totalDeals) * 100) : 0;

  // Remaining: all filtered deals not yet published
  const remainingCount = totalDeals - publishedDeals.length;

  // Avg Deal Size: average totalContractAmount per currency across all filtered deals
  const avgDealSizeByCurrency = {};
  const currencyTotals = {};
  filteredDeals.forEach((deal) => {
    const currency = deal.currency || 'USD';
    if (!currencyTotals[currency]) currencyTotals[currency] = { total: 0, count: 0 };
    currencyTotals[currency].total += deal.totalContractAmount || 0;
    currencyTotals[currency].count += 1;
  });
  Object.keys(currencyTotals).forEach((currency) => {
    avgDealSizeByCurrency[currency] = currencyTotals[currency].count > 0
      ? currencyTotals[currency].total / currencyTotals[currency].count
      : 0;
  });

  // Avg V30: average 30-day views across deals that have v30 data
  const dealsWithV30 = filteredDeals.filter(d => d.videoReportData?.v30 != null);
  const avgV30 = dealsWithV30.length > 0
    ? Math.round(dealsWithV30.reduce((sum, d) => sum + d.videoReportData.v30, 0) / dealsWithV30.length)
    : 0;

  return {
    booked: { byCurrency: bookedByCurrency, count: filteredDeals.length },
    published: { byCurrency: publishedByCurrency, count: publishedDeals.length },
    trackedCount,
    percentPublished,
    remaining: { count: remainingCount },
    avgDealSize: { byCurrency: avgDealSizeByCurrency },
    avgV30,
    percentNew: { percent: percentNew, count: newCreatorDeals.length },
    percentRebuy: { percent: percentRebuy, count: rebuyCreatorDeals.length },
  };
};

// Get program country options from HubSpot for filter dropdown
export const selectAvailableCountries = state => state.confirmedDeals.countryOptions;

export default confirmedDealsReducer.reducer;
